import { useCallback, useEffect, useState } from 'react';
import { Box, IconButton, Skeleton, Tooltip, Typography } from '@mui/material';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '../../kanban-board/crm-theme';
import { formatCrmDateTime } from '../../kanban-board/formatters/crm-datetime';
import { LeadBlock } from './lead-block';
import { LeadAttachment, LeadAttachmentService } from '../services/lead-attachment.service';
import { useLeadAttachmentUpload } from '../hooks/use-lead-attachment-upload.hook';
import { ELeadSignatureStatus, LeadSignature, LeadSignatureService } from '../services/lead-signature.service';
import { LeadStatusChip } from './lead-status-chip';
import { LeadSignatureModal } from './lead-signature-modal';

const formatSize = (bytes: number) =>
    bytes < 1024 * 1024
        ? `${Math.max(1, Math.round(bytes / 1024))} KB`
        : `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;

const isPdf = (file: LeadAttachment) =>
    file.contentType === 'application/pdf' || file.fileName.toLowerCase().endsWith('.pdf');

const SIGNATURE_STATUS: Record<ELeadSignatureStatus, { key: string; color: 'success' | 'warning' | 'default' }> = {
    [ELeadSignatureStatus.Sent]: { key: 'assinatura.aguardando', color: 'warning' },
    [ELeadSignatureStatus.Signed]: { key: 'assinatura.assinado', color: 'success' },
    [ELeadSignatureStatus.Cancelled]: { key: 'assinatura.cancelado', color: 'default' },
};

interface Props {
    leadId: string;
    columns?: number;
    /// Sobe quando o clipe do cabeçalho envia algo: a lista recarrega mesmo se foi aberta antes.
    version?: number;

    /// Sugestão de signatário ao mandar um PDF para assinatura.
    signerName?: string | null;
    signerEmail?: string | null;
}

/// Proposta, contrato, comprovante: a lista baixa e exclui; anexar é pelo clipe ou soltando o arquivo.
export const LeadAttachments = ({ leadId, columns, version = 0, signerName, signerEmail }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert, alertDelete } = useAlert();

    const [files, setFiles] = useState<LeadAttachment[] | null>(null);
    const [dragging, setDragging] = useState(false);
    const [canSign, setCanSign] = useState(false);
    const [signatures, setSignatures] = useState<LeadSignature[]>([]);
    const [signing, setSigning] = useState<LeadAttachment | null>(null);

    const load = useCallback(() => {
        new LeadAttachmentService()
            .list(leadId)
            .then((res) => setFiles(res.data.data ?? []))
            .catch(() => setFiles([]));

        new LeadSignatureService()
            .list(leadId)
            .then((res) => setSignatures(res.data.data ?? []))
            .catch(() => setSignatures([]));
    }, [leadId]);

    useEffect(load, [load, version]);

    useEffect(() => {
        new LeadSignatureService()
            .status()
            .then((res) => setCanSign(res.data.data?.configured ?? false))
            .catch(() => setCanSign(false));
    }, []);

    // A lista vem do mais novo ao mais antigo: o primeiro de cada anexo é o envio que vale.
    const latestSignature = (file: LeadAttachment) => signatures.find((s) => s.attachmentId === file.id);

    const signatureChip = (file: LeadAttachment) => {
        const signature = latestSignature(file);

        if (!signature) return null;

        const { key, color } = SIGNATURE_STATUS[signature.status];
        const total = signature.signers.length;

        // Com mais de uma pessoa, "aguardando" sozinho não diz quem falta.
        const label = signature.status === ELeadSignatureStatus.Sent && total > 1
            ? t('assinatura.aguardandoDe', {
                feitas: signature.signers.filter((s) => s.signedAt).length,
                total,
            })
            : t(key);

        return (
            <Tooltip title={signature.signers.map((s) => `${s.name}${s.signedAt ? ' ✓' : ''}`).join(', ')}>
                <span>
                    <LeadStatusChip label={label} color={color} />
                </span>
            </Tooltip>
        );
    };

    const { send } = useLeadAttachmentUpload(leadId, load);

    const download = async (file: LeadAttachment) => {
        try {
            const res = await new LeadAttachmentService().download(file.id);
            const url = URL.createObjectURL(res.data);
            const link = document.createElement('a');

            link.href = url;
            link.download = file.fileName;
            link.click();

            URL.revokeObjectURL(url);
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        }
    };

    const remove = (file: LeadAttachment) =>
        alertDelete(file.id, async () => {
            try {
                await new LeadAttachmentService().remove(file.id);
                load();
            } catch (err) {
                showAlert(apiErrorMessage(err), 'error', 4000);
            }
        }, file.fileName);

    return (
        <LeadBlock
            title={t('telaLead.arquivos')}
            count={files && files.length > 0 ? String(files.length) : undefined}
            columns={columns}
        >

            <Box
                onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setDragging(false);
                    send(e.dataTransfer.files);
                }}
                sx={{
                    flex: 1,
                    borderRadius: '9px',
                    border: `1px dashed ${dragging ? cc.accent : 'transparent'}`,
                    background: dragging ? cc.accentSoftBg : 'transparent',
                    transition: 'background .12s, border-color .12s',
                }}
            >
                {files === null ? (
                    <Skeleton variant="rounded" height={48} />
                ) : files.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: cc.textMuted, py: 0.5 }}>
                        {t('telaLead.semArquivos')}
                    </Typography>
                ) : (
                    files.map((file) => (
                        <Box
                            key={file.id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                py: 0.9,
                                '& + &': { borderTop: `1px solid ${cc.divider}` },
                            }}
                        >
                            <InsertDriveFileOutlinedIcon sx={{ fontSize: '18px !important', color: cc.textSecondary }} />

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    noWrap
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => download(file)}
                                    onKeyDown={(e) => e.key === 'Enter' && download(file)}
                                    sx={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: cc.accent,
                                        cursor: 'pointer',
                                        '&:hover': { textDecoration: 'underline' },
                                    }}
                                >
                                    {file.fileName}
                                </Typography>
                                <Typography noWrap sx={{ fontSize: 11.5, color: cc.textMuted }}>
                                    {formatSize(file.sizeBytes)} · {formatCrmDateTime(file.createdAt)}
                                    {file.uploadedByName ? ` · ${file.uploadedByName}` : ''}
                                </Typography>
                            </Box>

                            {signatureChip(file)}

                            {canSign && isPdf(file) && latestSignature(file)?.status !== ELeadSignatureStatus.Sent && (
                                <Tooltip title={t('assinatura.enviarParaAssinatura')}>
                                    <IconButton size="small" onClick={() => setSigning(file)} sx={{ color: cc.textMuted }}>
                                        <DrawOutlinedIcon sx={{ fontSize: '17px !important' }} />
                                    </IconButton>
                                </Tooltip>
                            )}

                            <Tooltip title={t('general.excluir')}>
                                <IconButton size="small" onClick={() => remove(file)} sx={{ color: cc.textMuted }}>
                                    <DeleteOutlineIcon sx={{ fontSize: '17px !important' }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ))
                )}
            </Box>

            <LeadSignatureModal
                leadId={leadId}
                attachment={signing}
                signerName={signerName}
                signerEmail={signerEmail}
                onClose={() => setSigning(null)}
                onSent={load}
            />

        </LeadBlock>
    );
};
